import { useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import { formatDate } from '../lib/dates';

/**
 * WHAT GOES IN THE FINANCE EXPORT, asked before it downloads.
 *
 * The button used to hand over every hour and every column, and the first thing done with the
 * file was deleting most of it in Excel. Three questions instead — which hours, which projects,
 * which columns — and the file is what was asked for. The rules are the server's
 * (`backend/src/time/time-export.ts`); this only asks and counts.
 */

export type ExportShow = 'all' | 'billable' | 'not_billable' | 'ready' | 'awaiting' | 'invoiced';

interface Row {
  projectCode: string;
  project: string;
  billable: boolean;
  status: string;
  invoicedAt: string | null;
}

const SHOWS: Array<{ key: ExportShow; label: string; hint: string }> = [
  { key: 'ready', label: 'Ready to invoice', hint: 'Billable, approved, not yet invoiced' },
  { key: 'billable', label: 'Billable', hint: 'Every billable hour, whatever its state' },
  { key: 'not_billable', label: 'Not billable', hint: 'Written off — the project or task says so' },
  { key: 'awaiting', label: 'Awaiting approval', hint: 'Billable, not approved yet' },
  { key: 'invoiced', label: 'Invoiced', hint: 'Already sent on an invoice' },
  { key: 'all', label: 'Everything', hint: 'Every hour booked' },
];

/** The file's columns, in the file's order — the keys are the server's. */
const COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'client', label: 'Client' },
  { key: 'date', label: 'Date' },
  { key: 'project', label: 'Project' },
  { key: 'projectCode', label: 'Project code' },
  { key: 'projectBillable', label: 'Project is billable' },
  { key: 'person', label: 'Person' },
  { key: 'task', label: 'Task' },
  { key: 'taskTitle', label: 'Task title' },
  { key: 'description', label: 'Description' },
  { key: 'hours', label: 'Hours' },
  { key: 'billable', label: 'Billable' },
  { key: 'status', label: 'Status' },
  { key: 'amount', label: 'Amount' },
  { key: 'invoice', label: 'To invoice (yes/no)' },
  { key: 'invoicedOn', label: 'Invoiced on' },
  { key: 'invoiceRef', label: 'Invoice ref' },
];

/** Shortcuts for the two files most often wanted; anything else is ticked by hand. */
const PRESETS: Array<{ label: string; keys: string[] }> = [
  { label: 'All columns', keys: COLUMNS.map((c) => c.key) },
  { label: 'For the invoice', keys: ['date', 'project', 'person', 'task', 'taskTitle', 'description', 'hours', 'amount'] },
  {
    label: 'Billable check',
    keys: ['date', 'project', 'projectBillable', 'person', 'task', 'taskTitle', 'hours', 'billable', 'status', 'invoice'],
  },
];

/** The same rules as `inExport` on the server, so the count on each choice is the file's. */
function inExport(row: Row, show: ExportShow): boolean {
  switch (show) {
    case 'all':
      return true;
    case 'billable':
      return row.billable;
    case 'not_billable':
      return !row.billable;
    case 'ready':
      return row.billable && row.status === 'approved' && !row.invoicedAt;
    case 'awaiting':
      return row.billable && !row.invoicedAt && row.status !== 'approved';
    case 'invoiced':
      return Boolean(row.invoicedAt);
  }
}

/** The columns last downloaded, remembered per browser so finance ticks them once. */
const COLUMNS_KEY = 'hub.timeExportColumns';

function rememberedColumns(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? 'null') as unknown;
    if (Array.isArray(saved)) {
      const known = saved.filter((key): key is string => COLUMNS.some((c) => c.key === key));
      if (known.length > 0) return known;
    }
  } catch {
    /* Private window or blocked storage: start from every column. */
  }
  return COLUMNS.map((c) => c.key);
}

interface Props {
  companyId: string;
  companyName: string;
  from: string;
  to: string;
  rows: Row[];
  /** Which tab the list was on, so the file starts as what was being looked at. */
  initialShow: ExportShow;
  onClose: () => void;
}

export function TimeLogExportModal({ companyId, companyName, from, to, rows, initialShow, onClose }: Props) {
  const [show, setShow] = useState<ExportShow>(initialShow);
  const projects = useMemo(() => {
    const byCode = new Map<string, string>();
    for (const row of rows) byCode.set(row.projectCode, row.project);
    return [...byCode].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const [pickedProjects, setPickedProjects] = useState<Set<string>>(() => new Set(projects.map((p) => p.code)));
  const [columns, setColumns] = useState<Set<string>>(() => new Set(rememberedColumns()));

  const allProjects = pickedProjects.size === projects.length;
  const kept = rows.filter((row) => inExport(row, show) && pickedProjects.has(row.projectCode));

  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  const query = new URLSearchParams({ from, to, show });
  /* Only narrowed when it is narrowed, so a project added tomorrow is not left out of a
     "everything" link somebody keeps. */
  if (!allProjects) query.set('projects', [...pickedProjects].join(','));
  query.set('columns', COLUMNS.filter((c) => columns.has(c.key)).map((c) => c.key).join(','));
  const href = `/api/companies/${companyId}/time-log.csv?${query.toString()}`;
  const ready = kept.length > 0 && columns.size > 0;

  return (
    <Modal
      title="Export for finance"
      subtitle={`${companyName} · ${formatDate(from)} to ${formatDate(to)}`}
      onClose={onClose}
      footer={
        <>
          <span className="dim" style={{ fontSize: 12, marginRight: 'auto' }}>
            {kept.length === 1 ? '1 line' : `${kept.length} lines`} · {columns.size} of {COLUMNS.length} columns
          </span>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          {/*
            A plain link, as before: the browser downloads it with the session it already has
            and the filename comes from the server. Closing after is a moment later, so the
            click is not lost with the element it landed on.
          */}
          <a
            className={ready ? 'btn btn-primary' : 'btn btn-primary is-disabled'}
            aria-disabled={!ready}
            href={ready ? href : undefined}
            onClick={(e) => {
              if (!ready) {
                e.preventDefault();
                return;
              }
              try {
                localStorage.setItem(COLUMNS_KEY, JSON.stringify([...columns]));
              } catch {
                /* Remembering is a convenience; the download does not depend on it. */
              }
              window.setTimeout(onClose, 0);
            }}
          >
            Download CSV
          </a>
        </>
      }
    >
      <div className="field">
        <span className="field-label">Which hours</span>
        <div className="export-choices" role="radiogroup" aria-label="Which hours">
          {SHOWS.map((option) => {
            const count = rows.filter((row) => inExport(row, option.key) && pickedProjects.has(row.projectCode)).length;
            return (
              <label key={option.key} className={show === option.key ? 'choice is-on' : 'choice'}>
                <input type="radio" name="export-show" checked={show === option.key} onChange={() => setShow(option.key)} />
                <span>
                  {option.label} <span className="lane-count">{count}</span>
                  <span className="choice-hint">{option.hint}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {projects.length > 1 && (
        <div className="field">
          <span className="field-label">
            Projects
            <button
              type="button"
              className="link-button"
              style={{ marginLeft: 8 }}
              onClick={() => setPickedProjects(allProjects ? new Set() : new Set(projects.map((p) => p.code)))}
            >
              {allProjects ? 'Clear' : 'All'}
            </button>
          </span>
          <div className="export-ticks">
            {projects.map((project) => (
              <label key={project.code} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={pickedProjects.has(project.code)}
                  onChange={() => setPickedProjects((set) => toggle(set, project.code))}
                />
                <span>{project.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="field" style={{ marginBottom: 0 }}>
        <span className="field-label">Columns</span>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {PRESETS.map((preset) => (
            <button key={preset.label} type="button" className="btn btn-sm" onClick={() => setColumns(new Set(preset.keys))}>
              {preset.label}
            </button>
          ))}
        </div>
        <div className="export-ticks">
          {COLUMNS.map((column) => (
            <label key={column.key} className="checkbox-row">
              <input type="checkbox" checked={columns.has(column.key)} onChange={() => setColumns((set) => toggle(set, column.key))} />
              <span>{column.label}</span>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
}
